import { Checkbox, CheckboxGroup, type CheckboxGroupProps, FieldHelper, Label, Text, TextSize } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { useLabel } from 'react-aria';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

interface FormCheckboxGroupProps<T extends FieldValues> extends CheckboxGroupProps {
  /**
   * The control object from the `useForm` hook.
   */
  control: Control<T>;
  /**
   * The description of the checkbox group.
   */
  description?: string;
  /**
   * The name of the field.
   */
  name: Path<T>;
  /**
   * The label of the checkbox group.
   */
  label?: string;
  /**
   * Whether the checkbox group is required.
   */
  isRequired?: boolean;
  /**
   * The children of the checkbox group.
   */
  children: React.ReactNode;
  /**
   * Whether the checkbox group has a select all checkbox.
   */
  hasSelectAll?: boolean;
}

function FormCheckboxGroup<T extends FieldValues>(props: FormCheckboxGroupProps<T>) {
  const { name, control, isDisabled, className, isRequired, label, children, description, hasSelectAll, ...other } =
    props;

  const { labelProps, fieldProps } = useLabel({ labelElementType: 'span', label });

  if (!children && React.Children.count(children) === 0) {
    throw new Error('Checkbox group must have at least one child Checkbox component');
  }

  const { field, fieldState, formState } = useController({ name, control });

  const defaultValue = React.useMemo(
    () => (formState?.defaultValues ? formState?.defaultValues[name] : undefined),
    [formState?.defaultValues, name],
  );

  const handleOnSelectAll = React.useCallback(
    (isSelected: boolean) => {
      field?.onChange(
        isSelected
          ? React.Children.toArray(children)
              ?.map((child) => {
                if (
                  React.isValidElement(child) &&
                  child.props &&
                  typeof child.props === 'object' &&
                  'value' in child.props
                ) {
                  return (child.props as { value: string }).value;
                }
                return null;
              })
              .filter(Boolean)
          : [],
      );
    },
    [field, children],
  );

  return (
    <div className={cx('flex flex-col gap-3 items-start justify-center', className)}>
      <Label elementType="span" {...labelProps} isRequired={isRequired}>
        {label}
      </Label>
      {hasSelectAll && (
        <Checkbox
          isDisabled={formState.isSubmitting || isDisabled}
          isIndeterminate={field?.value?.length !== 0 && field?.value?.length !== React.Children.count(children)}
          isSelected={field?.value?.length === React.Children.count(children)}
          value="all"
          onChange={handleOnSelectAll}
        >
          Select All
        </Checkbox>
      )}
      <CheckboxGroup
        {...fieldProps}
        className={cx(hasSelectAll && 'ml-6')}
        defaultValue={defaultValue}
        isDisabled={formState.isSubmitting || isDisabled}
        isInvalid={fieldState?.invalid}
        isRequired={isRequired}
        name={name}
        value={field?.value}
        onChange={field?.onChange}
        {...other}
      >
        {children}
      </CheckboxGroup>
      {description && <FieldHelper>{description}</FieldHelper>}
      <div className="min-h-[22px]">
        <Text size={TextSize.Sm} className={cx('text-center text-error-primary h-10', className)}>
          {fieldState?.error?.message}
        </Text>
      </div>
    </div>
  );
}

export { FormCheckboxGroup };
